import { researchLibrary } from '../data/researchLibrary';

const proofStats = [
  {
    value: researchLibrary.filter((entry) => entry.category === 'asi-original').length,
    label: 'ASI originals',
    detail: 'Independent essays and synthesis',
  },
  {
    value: researchLibrary.length,
    label: 'studies indexed',
    detail: 'Curated empirical and institutional sources',
  },
  {
    value: 3,
    label: 'research programs',
    detail: 'Performance, deployment, and risk',
  },
];

const researchPrograms = [
  {
    code: '01',
    title: 'AI Leverage & Human Performance',
    thesis:
      'Measure where AI changes outcomes, where it only changes perception, and why gains vary sharply across workers and teams.',
    relevance:
      'Covers workforce transition, training design, and productivity measurement.',
    links: ['AI Is an Attractor, Not a Multiplier', 'Why Most AI Training Programs Fail'],
  },
  {
    code: '02',
    title: 'Deployment Trajectories',
    thesis:
      'Separate capability narratives from field reality across robotics, autonomy, and enterprise AI implementation.',
    relevance:
      'Separates capability narratives from field reality for anyone making deployment calls.',
    links: ['Robots Aren\'t Ten Years Away', 'Enterprise AI implementation failure patterns'],
  },
  {
    code: '03',
    title: 'Dual-Use Autonomy & Emerging Risk',
    thesis:
      'Track how agentic software, robotics, and AI-assisted development reshape operational risk in sensitive domains.',
    relevance:
      'Maps how agentic software and robotics reshape operational risk in sensitive domains.',
    links: ['AI-assisted development risk', 'Autonomous systems threat surface'],
  },
];

function HeroSection() {
  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Enhanced JSON-LD Organization Schema for AI crawlers
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'ResearchOrganization',
    name: 'AI Studies Institute',
    alternateName: ['ASI', 'AI Studies Institute Research'],
    logo: {
      '@type': 'ImageObject',
      url: 'https://aistudies.institute/logo.png',
      width: 512,
      height: 512,
    },
    description: 'Independent research organization publishing rigorous essays on AI\'s effects on human performance, technology deployment trajectories, and dual-use autonomy risk, where the mainstream consensus is weak, incomplete, or behind the evidence.',
    email: 'contact@aistudies.institute',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Research Triangle Park',
      addressRegion: 'NC',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 35.8801,
      longitude: -78.7883,
    },
    knowsAbout: [
      'Human-AI Collaboration',
      'Individual Variance in AI Leverage',
      'AI Productivity Measurement',
      'Robotics Deployment',
      'Vision-Language-Action Models',
      'Dual-Use Autonomy',
      'AI-Assisted Development Risk',
    ],
    makesOffer: {
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: 'Independent Research Essays',
        description: 'Rigorous, evidence-first essays on AI-adjacent problems where the mainstream consensus is weak, incomplete, or behind the facts.',
        areaServed: 'Worldwide',
        provider: {
          '@type': 'ResearchOrganization',
          name: 'AI Studies Institute',
        },
      },
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'General Inquiries',
      email: 'contact@aistudies.institute',
      availableLanguage: ['English'],
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Research Areas',
      itemListElement: researchPrograms.map((program) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: program.title,
          description: program.thesis,
        },
      })),
    },
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'AI Studies Institute',
    publisher: {
      '@type': 'ResearchOrganization',
      name: 'AI Studies Institute',
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://aistudies.institute/?search={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <section
        id="home"
        className="relative min-h-[88vh] overflow-hidden bg-[#f7f8f6] pt-24 md:min-h-[90vh] md:pt-28"
      >
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(organizationSchema),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(websiteSchema),
          }}
        />

        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-x-0 top-0 h-px bg-slate-950/10" />
          <div className="absolute left-[8%] top-24 hidden h-[72%] w-px bg-slate-900/10 md:block" />
          <div className="absolute right-[12%] top-16 hidden h-[82%] w-px bg-slate-900/10 md:block" />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
        </div>

        <div className="container-asi relative grid min-h-[calc(88vh-7rem)] content-center gap-10 pb-12 md:min-h-[calc(90vh-7rem)] md:grid-cols-[1.1fr_0.9fr] md:pb-16">
          <div className="min-w-0 max-w-3xl">
            <p className="mb-5 max-w-full text-xs font-semibold uppercase tracking-[0.16em] text-asi-copper">
              Independent research / RTP
            </p>

            <h1 className="mb-6 max-w-[11ch] font-serif text-4xl font-light leading-[1.08] text-asi-navy sm:max-w-none sm:text-5xl md:text-7xl">
              AI Studies Institute
            </h1>

            <p className="mb-8 max-w-2xl break-words text-lg leading-8 text-slate-700 md:text-2xl md:leading-9">
              Evidence-first research on AI leverage, deployment reality, and
              emerging risk where the consensus is weak, incomplete, or behind
              the evidence.
            </p>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                onClick={() => scrollToSection('contact')}
                className="inline-flex min-h-12 w-full items-center justify-center border border-asi-navy bg-asi-navy px-6 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-asi-navy-dark sm:w-auto"
              >
                Get in Touch
              </button>
              <button
                onClick={() => scrollToSection('research')}
                className="inline-flex min-h-12 w-full items-center justify-center border border-slate-300 bg-white px-6 text-sm font-semibold uppercase tracking-wide text-asi-navy transition-colors hover:border-asi-copper hover:text-asi-copper sm:w-auto"
              >
                View Research
              </button>
            </div>
          </div>

          <aside className="min-w-0 border border-slate-200 bg-white/85 p-5 shadow-sm backdrop-blur md:p-6">
            <div className="mb-6 flex items-center justify-between border-b border-slate-200 pb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Institutional Signals
              </p>
              <span className="h-2 w-2 bg-asi-copper" aria-hidden="true" />
            </div>

            <dl className="grid gap-4">
              {proofStats.map((stat) => (
                <div
                  key={stat.label}
                  className="grid grid-cols-[56px_1fr] items-start gap-4 border-b border-slate-100 pb-4 last:border-b-0 last:pb-0 md:grid-cols-[72px_1fr]"
                >
                  <dt className="font-serif text-4xl leading-none text-asi-navy">
                    {stat.value}
                  </dt>
                  <dd>
                    <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-800">
                      {stat.label}
                    </p>
                    <p className="mb-0 break-words text-sm leading-6 text-slate-600">
                      {stat.detail}
                    </p>
                  </dd>
                </div>
              ))}
            </dl>

            <div className="mt-6 border-t border-slate-200 pt-5">
              <p className="mb-2 text-sm font-semibold text-asi-navy">
                Research focus
              </p>
              <p className="mb-0 break-words text-sm leading-6 text-slate-600">
                AI productivity measurement, deployment readiness, autonomy
                risk, and workforce transition.
              </p>
            </div>
          </aside>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white py-14 md:py-18">
        <div className="container-asi">
          <div className="mb-9 grid gap-4 md:grid-cols-[0.72fr_1fr] md:items-end">
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-asi-copper">
                Research Agenda
              </p>
              <h2 className="font-serif text-3xl text-asi-navy md:text-4xl">
                Three programs, one question
              </h2>
            </div>
            <p className="mb-0 max-w-2xl text-base leading-7 text-slate-600 md:justify-self-end">
              Where does AI actually change outcomes? ASI works the places where
              the consensus is weak and better synthesis changes what people
              decide.
            </p>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {researchPrograms.map((program) => (
              <article
                key={program.title}
                className="border border-slate-200 bg-[#fbfbfa] p-5 transition-colors hover:border-asi-copper"
              >
                <div className="mb-5 flex items-center justify-between border-b border-slate-200 pb-4">
                  <span className="font-serif text-2xl text-asi-copper">
                    {program.code}
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Program
                  </span>
                </div>
                <h3 className="mb-3 font-serif text-xl leading-snug text-asi-navy">
                  {program.title}
                </h3>
                <p className="text-sm leading-6 text-slate-700">
                  {program.thesis}
                </p>
                <p className="text-sm leading-6 text-slate-600">
                  {program.relevance}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {program.links.map((link) => (
                    <span
                      key={link}
                      className="border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600"
                    >
                      {link}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default HeroSection;
